/**
 * Org active pour l'impersonation par la console plateforme (PLATFORM_ADMIN).
 *
 * Quand un platform-admin « administre une org », le scope (id + nom) est stocké
 * ici et `httpClient` ajoute l'`id` en header `X-Org-Id` sur chaque requête
 * sécurisée — le backend, voyant le rôle PLATFORM_ADMIN, lève alors l'isolation
 * pour cette org. Pour tout utilisateur normal, ce scope n'est jamais défini.
 *
 * Store module-level (façon `authStorage`) car `httpClient` est une fonction, pas
 * un hook ; framework-agnostique (pas d'import React). `sessionStorage` : propre
 * à l'onglet, disparaît à la fermeture (plus sûr pour un accès aussi puissant).
 * Un mécanisme d'abonnement permet aux composants (bandeau, guard) de réagir.
 */
const ORG_SCOPE_KEY = 'platform:orgScope';

export type OrgScope = { id: string; name: string };

const listeners = new Set<() => void>();

function readRaw(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(ORG_SCOPE_KEY);
}

/** Parse le snapshot brut → scope complet (tolère l'ancien format « id nu »). */
export function parseOrgScope(raw: string | null): OrgScope | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && typeof v === 'object' && v.id) {
      return { id: String(v.id), name: String(v.name ?? '') };
    }
  } catch {
    return { id: raw, name: '' };
  }
  return null;
}

export const orgScope = {
  /** Snapshot brut (chaîne stable) — pour `useSyncExternalStore`. */
  getRaw: readRaw,
  /** Org active complète (id + nom), ou `null`. */
  get(): OrgScope | null {
    return parseOrgScope(readRaw());
  },
  /** Id de l'org active — lu par `httpClient` pour `X-Org-Id`. */
  getOrgId(): string | null {
    return parseOrgScope(readRaw())?.id ?? null;
  },
  /** Fixe (ou nettoie) l'org active et notifie les abonnés. */
  set(scope: OrgScope | null) {
    if (typeof window === 'undefined') return;
    if (scope?.id) {
      sessionStorage.setItem(
        ORG_SCOPE_KEY,
        JSON.stringify({ id: scope.id, name: scope.name ?? '' }),
      );
    } else {
      sessionStorage.removeItem(ORG_SCOPE_KEY);
    }
    listeners.forEach(l => l());
  },
  /** Compat : fixe par id seul (conserve le nom courant s'il existe). */
  setOrgId(orgId: string | null) {
    this.set(orgId ? { id: orgId, name: this.get()?.name ?? '' } : null);
  },
  /** Abonnement (même onglet via `listeners`, autres onglets via l'évènement `storage`). */
  subscribe(cb: () => void): () => void {
    listeners.add(cb);
    if (typeof window !== 'undefined') window.addEventListener('storage', cb);
    return () => {
      listeners.delete(cb);
      if (typeof window !== 'undefined') window.removeEventListener('storage', cb);
    };
  },
};
